import { useMemo, useState } from "react"

import { useFetchHomeGroups } from "src/data/homeGroups/queries/homeGroupQueries"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { langKeys } from "src/i18n/langKeys"
import { useTranslate } from "src/i18n/useTranslate"
import {
  DropdownSelect,
  TSelectOption,
} from "src/ui/DropdownSelect/DropdownSelect"

export function HomeGroupFilter({
  label,
  selectedHomeGroupId,
  onSelectionChange,
  placeholder,
}: {
  label: string
  selectedHomeGroupId?: string
  onSelectionChange: (
    args: {
      homeGroupId: string
      homeGroupName: string
    } | null
  ) => void
  placeholder?: string
}) {
  const { org } = useOrganization()
  const { t } = useTranslate()
  const orgId = org.id

  const [searchText, setSearchText] = useState("")

  const fetchHomeGroups = useFetchHomeGroups({
    orgId,
    filter: {
      name: searchText || undefined,
    },
    options: { keepPreviousData: true },
  })

  const groupedOptions = useMemo(() => {
    const homegroups = fetchHomeGroups.data?.homegroups || []

    return [
      {
        title: t(langKeys.home_group_building_plural),
        options: homegroups
          .filter((hg) => hg.type === "building")
          .map((hg) => ({
            value: hg.id,
            label: hg.name,
            homegroupName: hg.name,
          })),
      },
      {
        title: t(langKeys.organizations_home_group_plural),
        options: homegroups
          .filter((hg) => hg.type === "area")
          .map((hg) => ({
            value: hg.id,
            label: hg.name,
            homegroupName: hg.name,
          })),
      },
    ]
  }, [fetchHomeGroups.data?.homegroups, t])

  function handleChange({
    option,
  }: {
    option: TSelectOption<string, { homegroupName: string }>
  }) {
    onSelectionChange({
      homeGroupId: option.value,
      homeGroupName: option.homegroupName,
    })
  }

  function handleClearSelection() {
    onSelectionChange(null)
    setSearchText("")
  }

  return (
    <DropdownSelect<string, { homegroupName: string }>
      label={label}
      options={groupedOptions}
      selectedValue={selectedHomeGroupId ?? null}
      onChange={handleChange}
      onSearch={setSearchText}
      onClearSelection={handleClearSelection}
      loading={fetchHomeGroups.isLoading}
      placeholder={placeholder || t(langKeys.search)}
    />
  )
}
